import axios, {AxiosRequestConfig} from 'axios';
import {apiClient, getRequest, postRequest} from './client';
import {send} from 'process';
import getFormData from './getFormData';

export class ApiService {
  config: AxiosRequestConfig = {};

  private cancellationToken = axios.CancelToken.source();

  static createInstance(): ApiService {
    const activeInstance = new ApiService();
    activeInstance.cancellationToken = axios.CancelToken.source();
    activeInstance.config.cancelToken = activeInstance.cancellationToken?.token;
    return activeInstance;
  }

  cancelRequests() {
    this.cancellationToken.cancel('RequestCancellation');
    return ApiService.createInstance();
  }

  setToken(token?: string) {
    apiClient.defaults.headers.common['Authorization'] = token ? `Bearer ${token}` : undefined;
  }

  //// authentication
  auth = {
    currentUser: () => {
      return getRequest('/api/auth/user');
    },
    login: (username: string, password: string) => {
      return postRequest('/api/auth/login', {username, password});
    },
    loginToken: (username: string, password: string) => {
      return postRequest('/api/auth/loginToken', {username, password, device_name: 'web'});
    },
    logout: () => {
      return postRequest('/api/auth/logout');
    },
    logoutToken: () => {
      return postRequest('/api/auth/logoutToken');
    },
    userForgotPassword: (email: string) => {
      return postRequest('/api/auth/password/email', {email});
    },
    userResetPassword: (
      token: string,
      email: string,
      password: string,
      password_confirmation: string,
    ) => {
      return postRequest('/api/auth/password/reset', {
        token,
        email,
        password,
        password_confirmation,
      });
    },
    changePassword(oldPassword: string, password: string, password_confirmation: string) {
      return postRequest('/api/auth/changePassword', {
        oldPassword,
        password,
        password_confirmation,
      });
    },
  };

  users = {
    assignRoles(user_id: string | number, role_ids: number[]) {
      return postRequest(`/api/users/${user_id}/assignRoles`, {roles: role_ids});
    },
    changePassword(user_id: string | number, password: string, password_confirmation: string) {
      return postRequest(`/api/users/${user_id}/changePassword`, {password, password_confirmation});
    },
  };

  profile = {
    getProfile() {
      return getRequest('/api/profile');
    },
    saveProfile(payload: any) {
      return postRequest('/api/profile', payload);
    },
    getNotifications(type: 'all' | 'unread' | 'read' = 'all') {
      return getRequest(`/api/profile/notifications?type=${type}`);
    },
    markNotificationAsRead(ids: string[]) {
      return postRequest(`/api/profile/notifications/markAsRead`, {ids});
    },
  };

  projects = {
    loadWorkflow(project_id: string | number) {
      return getRequest(`/api/projects/${project_id}/workflow`);
    },
    saveWorkflow(project_id: string | number, workflow: any) {
      return postRequest(`/api/projects/${project_id}/workflow`, {workflow});
    },
    personalNotes: {
      load(project_id: string) {
        return getRequest(`/api/projects/${project_id}/personal-note`);
      },
      save(project_id: string, content: string) {
        return postRequest(`/api/projects/${project_id}/personal-note`, {content});
      },
    },
    estimate: {
      load(project_id: string) {
        return getRequest(`/api/projects/${project_id}/estimate`);
      },
      save(project_id: string, estimate_tree: any) {
        return postRequest(`/api/projects/${project_id}/estimate`, {estimate_tree});
      },
    },
    updateStatus(project_id: string, status: string) {
      return postRequest(`/api/projects/${project_id}/status`, {status});
    },
    summary() {
      return getRequest(`/api/projects-summary`);
    },
    projectSummary(project_id: string) {
      return getRequest(`/api/projects/${project_id}/summary`);
    },
  };

  tasks = {
    loadWorkflow(task_id: string | number) {
      return getRequest(`/api/tasks/${task_id}/workflow`);
    },
    saveWorkflow(task_id: string | number, workflow: any) {
      return postRequest(`/api/tasks/${task_id}/workflow`, {workflow});
    },
    estimate: {
      load(task_id: string) {
        return getRequest(`/api/tasks/${task_id}/estimate`);
      },
      save(task_id: string, estimate_tree: any) {
        return postRequest(`/api/tasks/${task_id}/estimate`, {estimate_tree});
      },
    },
    updateStatus(task_id: string, status: string) {
      return postRequest(`/api/tasks/${task_id}/status`, {status});
    },
    summary() {
      return getRequest(`/api/tasks-summary`);
    },
  };

  conversations = {
    sendMessage(conversation_id: string, message: string) {
      return postRequest(`/api/conversations/${conversation_id}/sendMessage`, {message});
    },
  };

  settings = {
    load(name: string) {
      return getRequest('/api/settings/' + name);
    },
    save(name: string, payload: any) {
      return postRequest('/api/settings/' + name, payload);
    },
  };

  documents = {
    upload(file: File, otherData: any) {
      const formdata = getFormData({file, ...otherData});

      return postRequest('/api/upload/document', formdata, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
  };

  getRequest = getRequest;
  postRequest = postRequest;
}

export const apiService = ApiService.createInstance();
if (process.env.NODE_ENV === 'development') (window as any).api = apiService;

export const useApiService = () => apiService;
// export const useApiService = () => ApiService.createInstance();
